import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Loader2, FileText, Check } from "lucide-react";
import { pdf } from "@react-pdf/renderer";
import { TEMPLATES } from "./templates/registry";
import { useResumeData } from "./useResumeData";

/* ─── Live thumbnail preview (miniature CSS mock of each template) ─── */
const TemplateThumbnail = ({ tmpl, active }) => {
  const { preview } = tmpl;
  const scale = 0.88;

  if (preview.style === "twocol") {
    return (
      <div
        className="w-full aspect-[210/297] rounded-lg overflow-hidden shadow-xl transition-transform"
        style={{
          background: preview.background,
          transform: active ? "scale(1.02)" : "scale(1)",
          boxShadow: active ? `0 0 0 2px ${preview.accent}, 0 20px 40px rgba(0,0,0,0.5)` : "0 12px 24px rgba(0,0,0,0.35)",
        }}
      >
        <div className="flex h-full">
          {/* Sidebar */}
          <div style={{ background: preview.sidebar, color: preview.sidebarText, width: "34%" }} className="p-3 space-y-3">
            <div style={{ fontFamily: preview.titleFont, fontSize: 10, fontWeight: 700, color: preview.sidebarText, lineHeight: 1.1 }}>
              Utkarsh Bokadia
            </div>
            <div style={{ fontSize: 5, color: preview.accent, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>
              Contact
            </div>
            <div style={{ fontSize: 5, color: preview.sidebarText, opacity: 0.8, lineHeight: 1.4 }}>
              Ahmedabad<br />email@…<br />linkedin.com/…
            </div>
            <div style={{ fontSize: 5, color: preview.accent, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>
              Skills
            </div>
            <div style={{ fontSize: 5, color: preview.sidebarText, opacity: 0.75, lineHeight: 1.5 }}>
              AI · UI/UX · Trading · Risk · Wealth · Networks
            </div>
          </div>
          {/* Main */}
          <div className="flex-1 p-3 space-y-2" style={{ color: preview.color }}>
            <div style={{ fontSize: 5, color: preview.color, opacity: 0.7, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>Experience</div>
            {[0, 1, 2].map((i) => (
              <div key={i}>
                <div style={{ fontSize: 6, fontWeight: 600, color: preview.color }}>Company Name</div>
                <div style={{ fontSize: 5, color: preview.body }}>Role · Span</div>
                <div style={{ fontSize: 5, color: preview.body, lineHeight: 1.5, marginTop: 2 }}>
                  ●●●●●●●●●●●●●●●●●●●●●●●●●●
                </div>
              </div>
            ))}
            <div style={{ background: "#e6e6e8", height: 0.5, marginTop: 4 }} />
            <div style={{ fontSize: 5, color: preview.color, opacity: 0.7, letterSpacing: "0.15em", textTransform: "uppercase", fontWeight: 700 }}>Credentials</div>
            {[0, 1].map((i) => (
              <div key={i}>
                <div style={{ fontSize: 6, fontWeight: 600 }}>Certificate Title</div>
                <div style={{ fontSize: 5, color: preview.body }}>Issuer</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (preview.style === "light") {
    return (
      <div
        className="w-full aspect-[210/297] rounded-lg overflow-hidden p-4 shadow-xl transition-transform"
        style={{
          background: preview.background,
          color: preview.color,
          transform: active ? "scale(1.02)" : "scale(1)",
          boxShadow: active ? `0 0 0 2px ${preview.accent}, 0 20px 40px rgba(0,0,0,0.35)` : "0 12px 24px rgba(0,0,0,0.35)",
        }}
      >
        <div style={{ borderBottom: `0.5px solid ${preview.color}`, paddingBottom: 8, textAlign: "center" }}>
          <div style={{ fontFamily: preview.titleFont, fontSize: 11, fontWeight: 700, letterSpacing: "0.3em", textTransform: "uppercase" }}>
            Utkarsh Bokadia
          </div>
          <div style={{ fontSize: 5, fontStyle: "italic", color: preview.body, marginTop: 2 }}>
            Entrepreneur · Investor · Strategist
          </div>
        </div>
        <div style={{ fontSize: 4.5, fontStyle: "italic", textAlign: "center", marginTop: 4, color: preview.body }}>
              "Every destination tells a story."
        </div>
        <div style={{ marginTop: 8 }}>
          <div style={{ fontSize: 6, fontWeight: 600, letterSpacing: "0.25em", textTransform: "uppercase", borderBottom: `0.4px solid ${preview.color}`, paddingBottom: 2 }}>
            Experience
          </div>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} style={{ marginTop: 4 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 6 }}>
                <span style={{ fontWeight: 600 }}>Company</span>
                <span style={{ fontStyle: "italic", color: preview.body }}>Span</span>
              </div>
              <div style={{ fontSize: 5, color: preview.body, fontStyle: "italic" }}>Role</div>
              <div style={{ fontSize: 5, color: preview.body, lineHeight: 1.5, marginTop: 1 }}>
                ●●●●●●●●●●●●●●●●●●●●●●●●●●●●●●
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Dark editorial style
  return (
    <div
      className="w-full aspect-[210/297] rounded-lg overflow-hidden p-4 shadow-xl transition-transform"
      style={{
        background: preview.background,
        color: preview.color,
        transform: active ? "scale(1.02)" : "scale(1)",
        boxShadow: active ? `0 0 0 2px ${preview.accent}, 0 20px 40px rgba(0,0,0,0.5)` : "0 12px 24px rgba(0,0,0,0.4)",
      }}
    >
      <div style={{ borderBottom: "0.5px solid #1a1e28", paddingBottom: 8 }}>
        <div style={{ fontSize: 4.5, color: preview.accent, letterSpacing: "0.35em", textTransform: "uppercase", fontFamily: "monospace" }}>
          Personal Archive · Résumé
        </div>
        <div style={{ fontFamily: preview.titleFont, fontSize: 14, marginTop: 3, letterSpacing: "-0.02em" }}>
          Utkarsh Bokadia
        </div>
        <div style={{ fontSize: 5, color: preview.body, marginTop: 2 }}>Distributor · Incoming Director · FYSE Ventures SRL</div>
      </div>
      <div style={{ fontFamily: preview.titleFont, fontStyle: "italic", fontSize: 6.5, color: "#d0d2d8", marginTop: 6, lineHeight: 1.5 }}>
        &ldquo;Every destination tells a story.&rdquo;
      </div>
      <div style={{ marginTop: 6 }}>
        <div style={{ fontSize: 4.5, color: preview.accent, letterSpacing: "0.3em", textTransform: "uppercase", fontFamily: "monospace" }}>
          01 · Experience
        </div>
        <div style={{ fontFamily: preview.titleFont, fontSize: 8, marginTop: 2 }}>
                Where I&apos;ve served the work.
        </div>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ display: "flex", marginTop: 4, gap: 6 }}>
            <div style={{ width: 30, fontSize: 4.5, color: "#7a7d87", fontFamily: "monospace", letterSpacing: "0.1em" }}>2025—26</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 6, fontWeight: 600 }}>Company Name</div>
              <div style={{ fontSize: 5, color: preview.body }}>Role · Location</div>
              <div style={{ fontSize: 4.5, color: "#7a7d87", lineHeight: 1.5, marginTop: 1 }}>
                ●●●●●●●●●●●●●●●●●●●●●●●●●●●●
              </div>
              <div style={{ display: "flex", gap: 3, marginTop: 3, flexWrap: "wrap" }}>
                {["AI", "UX", "Trade"].map((s) => (
                  <span key={s} style={{ fontSize: 3.5, color: preview.accent, border: `0.4px solid ${preview.accent}55`, padding: "1.5px 3px", borderRadius: 6, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "monospace" }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Main modal ─── */
const ResumeModal = ({ open, onClose }) => {
  const data = useResumeData();
  const [selected, setSelected] = React.useState(TEMPLATES[0].key);
  const [generating, setGenerating] = React.useState(false);

  const download = async () => {
    const tmpl = TEMPLATES.find((t) => t.key === selected);
    setGenerating(true);
    try {
      const Doc = tmpl.component;
      const blob = await pdf(
        <Doc
          profile={data.profile}
          credentials={data.credentials}
          awards={data.awards}
          experiences={data.experiences}
          projects={data.projects}
          skills={data.skills}
          places={data.places}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Utkarsh-Bokadia_${tmpl.key}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("PDF generation failed", e);
      alert("Sorry — couldn't generate the PDF. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-2xl flex items-center justify-center p-4 md:p-8 overflow-y-auto"
          onClick={onClose}
          data-testid="resume-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 20 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[1100px] rounded-[24px] border border-white/10 bg-gradient-to-br from-[#0c0d12] to-[#06070b] p-8 md:p-10 my-auto"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-10 h-10 grid place-items-center rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white"
              data-testid="resume-modal-close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="text-[11px] tracking-[0.4em] uppercase text-[#e6c769]/80 font-mono mb-3">
                Choose your design
              </div>
              <h2
                className="text-[32px] md:text-[48px] leading-tight text-white"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Download your resume.
              </h2>
              <p className="mt-2 text-[14px] text-white/55 max-w-xl mx-auto">
                Auto-generated live from this site — every credential, role, project, and skill you&rsquo;ve added.
                Pick the design that fits the room you&rsquo;re walking into.
              </p>
            </div>

            {/* Template picker grid */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {TEMPLATES.map((t) => {
                const isActive = t.key === selected;
                return (
                  <button
                    key={t.key}
                    onClick={() => setSelected(t.key)}
                    data-testid={`resume-template-${t.key}`}
                    className="group text-left focus:outline-none"
                  >
                    <TemplateThumbnail tmpl={t} active={isActive} />
                    <div className="mt-4 flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="text-[15px] font-medium text-white">{t.name}</div>
                          {isActive && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] tracking-[0.2em] uppercase font-mono"
                              style={{ background: `${t.accent}18`, color: t.accent, border: `1px solid ${t.accent}44` }}
                            >
                              <Check className="w-2.5 h-2.5" strokeWidth={3} /> Selected
                            </span>
                          )}
                        </div>
                        <div className="text-[12px] text-white/50 mt-0.5">{t.tagline}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* CTA row */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/10">
              <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-white/40 font-mono">
                <FileText className="w-3.5 h-3.5" />
                {data.loading ? "Loading your data…" : `${data.credentials.length + data.awards.length + data.experiences.length + data.projects.length} items · ${data.skills.length} skills · ${data.places.length} places`}
              </div>
              <button
                onClick={download}
                disabled={generating || data.loading}
                data-testid="resume-download"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#e6c769] text-black text-[12px] tracking-[0.22em] uppercase font-medium hover:scale-[1.03] transition-transform disabled:opacity-50"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Download PDF
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ResumeModal;
